const UPDATED_DATE = '2026-10-31';

const EDITORIAL_META = {
  author: 'Acne Safe Editorial Team',
  reviewedBy: 'Acne Safe Research Team',
  updatedDate: UPDATED_DATE,
  reviewNote: 'Product details and linked evidence reviewed October 31, 2026. Formulas can change; verify the current package ingredient list before buying.',
};

const PRIMARY_ARTICLES = {
  'acne-safe-makeup': {
    title: 'Acne Safe Makeup: Foundation, Concealer & Primer Compared',
    description: 'Compare makeup for acne-prone skin by format, finish, coverage, and removal. See best-for picks and learn how to check each current ingredient list.',
    keywords: 'acne safe makeup, makeup for acne prone skin, non comedogenic foundation, acne safe concealer, acne safe primer',
    comparison: `
      <section class="article-comparison" aria-labelledby="makeup-comparison-heading">
        <h2 id="makeup-comparison-heading">Acne-prone makeup comparison: best for each need</h2>
        <p><strong>How we classified these:</strong> by format, finish, coverage, and the ingredient information available when this guide was reviewed—not by a guarantee that a finished product cannot cause breakouts. Formulas vary by shade and can change.</p>
        <div class="article-comparison-table-wrap">
          <table class="article-comparison-table">
            <thead><tr><th>Product</th><th>Best for</th><th>Format / finish</th><th>Check before buying</th></tr></thead>
            <tbody>
              <tr><td>bareMinerals Original Loose Powder Foundation</td><td>Minimal formulas and buildable coverage</td><td>Loose mineral powder / natural</td><td>Current shade-specific ingredient list</td></tr>
              <tr><td>Clinique Even Better Clinical Serum Foundation</td><td>Liquid coverage with a lighter feel</td><td>Liquid / natural-matte</td><td>Full formula and personal sensitivity</td></tr>
              <tr><td>Neutrogena SkinClearing Mineral Powder</td><td>Oily skin that prefers powder</td><td>Pressed powder / matte</td><td>Salicylic-acid tolerance and current label</td></tr>
              <tr><td>NYX HD Photogenic Concealer</td><td>Budget-friendly spot concealing</td><td>Liquid concealer / natural</td><td>Shade formula and fragrance sensitivity</td></tr>
              <tr><td>e.l.f. Power Grip Primer</td><td>Grip under longer-wear makeup</td><td>Water-based gel / dewy</td><td>Compatibility with your foundation and current INCI</td></tr>
            </tbody>
          </table>
        </div>
        <p><a href="/acne-safe-ingredient-checker">Check the current full label with the Acne Safe Ingredient Checker</a>.</p>
      </section>`,
  },
  'best-moisturizers-acne-prone-skin': {
    title: '10 Best Moisturizers for Acne-Prone Skin Compared',
    description: 'Compare lightweight, barrier-supporting, and treatment-friendly moisturizers for acne-prone skin, with best-for picks and current-label checks.',
    keywords: 'best moisturizer for acne prone skin, non comedogenic moisturizer, moisturizer for oily acne prone skin, acne treatment moisturizer',
    comparison: `
      <section class="article-comparison" aria-labelledby="moisturizer-comparison-heading">
        <h2 id="moisturizer-comparison-heading">Moisturizer comparison: best for each skin need</h2>
        <p><strong>How we classified these:</strong> by texture, barrier-supporting ingredients, and the ingredient information available when this guide was reviewed. “Best for” is a starting point, not a promise that every skin type will tolerate a formula.</p>
        <div class="article-comparison-table-wrap">
          <table class="article-comparison-table">
            <thead><tr><th>Product</th><th>Best for</th><th>Texture / notable features</th><th>Check before buying</th></tr></thead>
            <tbody>
              <tr><td>Vanicream Daily Facial Moisturizer</td><td>Sensitive, easily irritated skin</td><td>Light lotion / ceramides and hyaluronic acid</td><td>Current full ingredient list</td></tr>
              <tr><td>Neutrogena Hydro Boost Water Gel, Fragrance-Free</td><td>Oily skin wanting light hydration</td><td>Water gel / hyaluronic acid</td><td>Choose the fragrance-free version</td></tr>
              <tr><td>La Roche-Posay Toleriane Double Repair</td><td>Barrier support during acne treatment</td><td>Lotion-cream / ceramide and niacinamide</td><td>Current regional formula</td></tr>
              <tr><td>Paula's Choice Skin Balancing Moisture Gel</td><td>Combination skin and a matte finish</td><td>Gel / niacinamide and antioxidants</td><td>Current label and individual sensitivity</td></tr>
              <tr><td>Illiyoon Ceramide Ato Concentrate Cream</td><td>Dryness from retinoids or winter weather</td><td>Rich cream / ceramides</td><td>Patch-test richer textures</td></tr>
            </tbody>
          </table>
        </div>
        <p><a href="/acne-safe-ingredient-checker">Check the current full label with the Acne Safe Ingredient Checker</a>.</p>
      </section>`,
  },
  'best-sunscreen-for-acne-prone-skin': {
    title: '10 Best Sunscreens for Acne-Prone Skin Compared',
    description: 'Compare mineral, chemical, tinted, and water-resistant sunscreens for acne-prone skin, with best-for picks, label checks, and formula caveats.',
    keywords: 'best sunscreen for acne prone skin, sunscreen that does not clog pores, non comedogenic sunscreen, mineral sunscreen for acne',
    comparison: `
      <section class="article-comparison" aria-labelledby="sunscreen-comparison-heading">
        <h2 id="sunscreen-comparison-heading">Sunscreen comparison: best for each preference</h2>
        <p><strong>How we classified these:</strong> by UV-filter type, finish, tint, and the ingredient information available when this guide was reviewed. Sunscreen performance depends on applying enough and reapplying; formulas and active filters vary by country.</p>
        <div class="article-comparison-table-wrap">
          <table class="article-comparison-table">
            <thead><tr><th>Product</th><th>Best for</th><th>Filter / finish</th><th>Check before buying</th></tr></thead>
            <tbody>
              <tr><td>EltaMD UV Clear SPF 46</td><td>Daily wear with visible redness</td><td>Hybrid / lightweight</td><td>Tinted vs. untinted formula and current label</td></tr>
              <tr><td>La Roche-Posay Anthelios Light Fluid SPF 50</td><td>Layering beneath makeup</td><td>Chemical / fluid</td><td>Regional filter system and alcohol sensitivity</td></tr>
              <tr><td>Australian Gold Botanical Tinted Face SPF 50</td><td>Tinted mineral coverage and a matte finish</td><td>Mineral / tinted matte</td><td>Tint match and dryness</td></tr>
              <tr><td>ISNTREE Hyaluronic Acid Watery Sun Gel SPF 50+</td><td>Light hydration without a mineral cast</td><td>Chemical / dewy gel</td><td>Country-specific formula and current label</td></tr>
              <tr><td>Neutrogena Clear Face Liquid Lotion SPF 55</td><td>Outdoor use and a widely available option</td><td>Chemical / lotion</td><td>Eye sensitivity and current inactive ingredients</td></tr>
            </tbody>
          </table>
        </div>
        <p><a href="/acne-safe-ingredient-checker">Check the current full label with the Acne Safe Ingredient Checker</a>.</p>
      </section>`,
  },
};

const ARTICLE_CLUSTERS = {
  'acne-safe-makeup': [
    'foundation-that-doesnt-clog-pores',
    'does-foundation-cause-acne',
    'liquid-vs-powder-vs-stick-foundation-acne',
    'primer-for-acne-prone-skin',
    'acne-safe-mineral-makeup',
    'makeup-causing-forehead-cheek-breakouts',
  ],
  'best-moisturizers-acne-prone-skin': [
    'gel-vs-cream-moisturizer-acne-prone-skin',
    'acne-safe-moisturizer-with-spf',
  ],
  'best-sunscreen-for-acne-prone-skin': [
    'can-sunscreen-cause-breakouts',
    'spf-50-sunscreen-wont-clog-pores',
    'zinc-oxide-sunscreen-acne',
  ],
};

const CONSOLIDATED_SLUGS = new Set([
  'acne-safe-makeup-foundation',
  'best-acne-safe-moisturizers-2025',
  'non-comedogenic-sunscreen-guide',
]);

const PRIMARY_LINK_LABELS = {
  'acne-safe-makeup': 'Compare foundation, concealer, and primer in our complete acne-safe makeup guide',
  'best-moisturizers-acne-prone-skin': 'Compare textures and best-for picks in our moisturizer guide for acne-prone skin',
  'best-sunscreen-for-acne-prone-skin': 'Compare mineral, chemical, and tinted options in our sunscreen guide for acne-prone skin',
};

export function applyArticleSeoEnhancements(posts) {
  for (let index = posts.length - 1; index >= 0; index -= 1) {
    if (CONSOLIDATED_SLUGS.has(posts[index].slug)) posts.splice(index, 1);
  }

  const bySlug = new Map(posts.map((post) => [post.slug, post]));

  Object.entries(PRIMARY_ARTICLES).forEach(([slug, enhancement]) => {
    const post = bySlug.get(slug);
    if (!post) return;

    Object.assign(post, EDITORIAL_META, {
      title: enhancement.title,
      description: enhancement.description,
      keywords: enhancement.keywords,
    });

    if (!post.content.includes('class="article-comparison"')) {
      post.content = `${enhancement.comparison}${post.content}`;
    }
  });

  Object.entries(ARTICLE_CLUSTERS).forEach(([primarySlug, relatedSlugs]) => {
    relatedSlugs.forEach((relatedSlug) => {
      const post = bySlug.get(relatedSlug);
      if (!post || post.content.includes('class="article-primary-link"')) return;
      post.content = `
        <aside class="article-primary-link" aria-label="Primary topic guide">
          <strong>Start with the complete guide:</strong>
          <a href="/blog/${primarySlug}">${PRIMARY_LINK_LABELS[primarySlug]}</a>.
        </aside>
        ${post.content}`;
    });
  });

  return posts;
}
